import { divHalfUp, roundToEnding, type EndingRound } from './decimal.ts';

/**
 * Rechenkern „VK netto aus Basisbetrag × Faktor“ (Story 5.59, Massenbearbeitung „aus EK berechnen“). Rein: ohne
 * Datenbank, Uhr und Netz; Basisbetrag, Kurse samt Kursdatum, Faktor und Endung kommen als Eingabe (AD-27).
 *
 * Der Kern kennt keinen EK: Basis ist ein beliebiger Betrag mit Währung. Heute füllt `reporting` sie mit dem EK des
 * Hauptlieferanten; eine spätere Story kann einen Einstandspreis hineingeben, ohne den Kern zu ändern.
 *
 * Rechnung in ganzen Zahlen (nie Gleitkomma):
 * 1. Basis in 1e-4 der Basiswährung (mehr Nachkommastellen: half-up auf 4).
 * 2. Fremdwährung → Preiswährung (nur nach EUR): Kurs = Fremdwährung je 1 EUR (EZB-Referenzkurs),
 *    Basis € = Basis ÷ Kurs, 4 Nachkommastellen half-up.
 * 3. Zwischenwert in Cent = Basis € × Faktor, half-up auf Cent.
 * 4. Endung ,90 · ,99 · ganze: nächstgelegener Wert mit der Endung, Gleichstand aufwärts (wie „Runden“ der Liste).
 * Ergebnis ≤ 0 (Zwischenwert oder nach Endung) → übersprungen.
 * Story 5.60: zusätzlich `units4` (Ergebnis in 10⁻⁴ € für VK-Spalten mit 4 Nachkommastellen): mit Endung = Cent × 100,
 * ohne Endung (`round` fehlt) Basis € × Faktor half-up auf 4 Nachkommastellen.
 *
 * Abnahme durch Steuerberater/Rudolf nötig (Preislogik).
 */

/** Version der Rechenregel; steht an jedem Ergebnis (AD-27). */
export const PURCHASE_PRICE_RULE_VERSION = 1;

/** Basisbetrag: Dezimaltext mit Punkt oder Komma (z. B. „21.4000“), Währung ISO 4217. */
export interface PurchasePriceBase {
  amount: string | null;
  currency: string;
}

/** Kurs je Fremdwährung: Einheiten der Fremdwährung je 1 EUR (Dezimaltext) und Kursdatum (ISO `YYYY-MM-DD`). */
export interface PurchasePriceRate {
  rate: string;
  date: string;
}

export interface PurchasePriceInput {
  base: PurchasePriceBase | null;
  /** Währung des VK (heute immer EUR). */
  priceCurrency: string;
  rates: Readonly<Record<string, PurchasePriceRate>>;
  /** Faktor als Dezimaltext (Punkt oder Komma), > 0, höchstens 4 Nachkommastellen (Obergrenze prüft der Aufrufer aus `settings`). */
  factor: string;
  /** Endung; fehlt sie (Story 5.60), wird nur half-up gerundet (Cent bzw. 4 Nachkommastellen). */
  round?: EndingRound;
}

export type PurchasePriceResult =
  | { kind: 'ok'; cents: bigint; units4: bigint; explain: string; version: number }
  | { kind: 'skip'; reason: string; version: number };

export const PURCHASE_PRICE_TEXTS = {
  noBase: 'übersprungen: kein EK beim Hauptlieferanten',
  noRate: (currency: string) => `übersprungen: kein EZB-Kurs für ${currency}`,
  notPositive: 'übersprungen: Ergebnis 0 € oder weniger',
  factorInvalid: 'Bitte einen Faktor größer als 0 eingeben, z. B. 1,8 (höchstens 4 Nachkommastellen).',
  factorTooLarge: (max: string) => `Der Faktor darf höchstens ${max} sein (Einstellungen › Artikel › Preise).`,
} as const;

const SCALE = 4;
const TEN4 = 10n ** BigInt(SCALE);
const DECIMAL = /^(\d{1,15})(?:[.,](\d{1,8}))?$/;

/** Nicht-negativer Dezimaltext → Einheiten von 1e-4 (half-up); sonst `null`. `strict`: mehr als 4 Stellen = `null`. */
function toUnits4(text: string, strict: boolean): bigint | null {
  const m = DECIMAL.exec(text.trim());
  if (!m) return null;
  const frac = m[2] ?? '';
  if (strict && frac.length > SCALE) return null;
  const units = BigInt(`${m[1]}${frac}`);
  return frac.length <= SCALE ? units * 10n ** BigInt(SCALE - frac.length) : divHalfUp(units, 10n ** BigInt(frac.length - SCALE));
}

/**
 * Faktor prüfen: > 0, höchstens 4 Nachkommastellen und – wenn `max` angegeben – höchstens `max` (Obergrenze als
 * Schutz vor Tippfehlern wie 180 statt 1,80; kommt versioniert aus `settings`, nie als Konstante). Ergebnis in 1e-4 oder
 * `null`.
 */
export function parsePurchasePriceFactor(text: unknown, max?: string): bigint | null {
  if (typeof text !== 'string') return null;
  const units = toUnits4(text, true);
  if (units === null || units <= 0n) return null;
  if (max !== undefined) {
    const limit = toUnits4(max, true);
    if (limit === null || units > limit) return null;
  }
  return units;
}

/** Einheiten von 1e-4 → Dezimaltext mit Punkt, ohne überflüssige Nullen (z. B. 18000 → „1.8“). */
export function formatFactor(units: bigint): string {
  const int = units / TEN4;
  const frac = (units % TEN4).toString().padStart(SCALE, '0').replace(/0+$/, '');
  return frac ? `${int}.${frac}` : `${int}`;
}

const grouping = new Intl.NumberFormat('de-DE');

/** 1e-`scale`-Einheiten (nicht negativ) → deutsch, mindestens `minFrac` und höchstens `scale` Nachkommastellen. */
function german(units: bigint, scale: number, minFrac: number): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const div = 10n ** BigInt(scale);
  let frac = (abs % div).toString().padStart(scale, '0');
  while (frac.length > minFrac && frac.endsWith('0')) frac = frac.slice(0, -1);
  return `${negative ? '−' : ''}${grouping.format(abs / div)}${frac ? `,${frac}` : ''}`;
}

function germanDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Ergebnis der Umrechnung eines EK in Euro (1e-4-Einheiten), mit Kurs und Kursdatum bei Fremdwährung. */
export type PurchaseToEurResult =
  | { kind: 'ok'; units4: bigint; amount4: bigint; currency: string; fx: { rate4: bigint; rate: string; date: string } | null }
  | { kind: 'skip'; status: 'no_ek' | 'no_fx'; currency: string };

/**
 * Einzige Umrechnung eines Einkaufsbetrags in die Preiswährung (heute EUR; Story 5.59, mitbenutzt vom Einstandspreis
 * Story 5.84): Betrag in 1e-4 (half-up), Fremdwährung ÷ EZB-Kurs (Fremdwährung je 1 EUR), 4 Nachkommastellen half-up.
 * Ohne Betrag `no_ek`, ohne gültigen Kurs `no_fx`. Rein (AD-27). Abnahme durch Steuerberater/Rudolf nötig.
 */
export function convertPurchaseToEur(
  base: PurchasePriceBase | null,
  rates: Readonly<Record<string, PurchasePriceRate>>,
  priceCurrency = 'EUR',
): PurchaseToEurResult {
  const amount = base && base.amount !== null ? toUnits4(base.amount, false) : null;
  if (!base || amount === null) return { kind: 'skip', status: 'no_ek', currency: base?.currency ?? priceCurrency };
  if (base.currency === priceCurrency) return { kind: 'ok', units4: amount, amount4: amount, currency: base.currency, fx: null };
  const rate = priceCurrency === 'EUR' ? rates[base.currency] : undefined;
  const rate4 = rate ? toUnits4(rate.rate, false) : null;
  if (!rate || rate4 === null || rate4 <= 0n) return { kind: 'skip', status: 'no_fx', currency: base.currency };
  return { kind: 'ok', units4: divHalfUp(amount * TEN4, rate4), amount4: amount, currency: base.currency, fx: { rate4, rate: rate.rate, date: rate.date } };
}

/** Nicht-negativer Dezimaltext (Punkt oder Komma) → Einheiten von 1e-4, half-up; sonst `null`. */
export function decimalToUnits4(text: string): bigint | null {
  return toUnits4(text, false);
}

/** Neuer VK in Cent aus Basis × Faktor mit Endung, oder Grund, warum die Zeile übersprungen wird. */
export function computePriceFromPurchase(input: PurchasePriceInput): PurchasePriceResult {
  const version = PURCHASE_PRICE_RULE_VERSION;
  const factor = parsePurchasePriceFactor(input.factor);
  if (factor === null) throw new Error(PURCHASE_PRICE_TEXTS.factorInvalid);
  const converted = convertPurchaseToEur(input.base, input.rates, input.priceCurrency);
  if (converted.kind === 'skip') {
    return { kind: 'skip', reason: converted.status === 'no_ek' ? PURCHASE_PRICE_TEXTS.noBase : PURCHASE_PRICE_TEXTS.noRate(converted.currency), version };
  }
  const baseEur = converted.units4;
  const steps: string[] = [];
  if (converted.fx) {
    steps.push(`${german(converted.amount4, SCALE, 2)} ${converted.currency} ÷ ${german(converted.fx.rate4, SCALE, 0)} (EZB ${germanDate(converted.fx.date)}) = ${german(baseEur, SCALE, 2)} €`);
  } else {
    steps.push(`${german(converted.amount4, SCALE, 2)} €`);
  }

  // Basis (1e-4 €) × Faktor (1e-4) = 1e-8 €. Die Endung wird direkt vom ungerundeten Produkt
  // bestimmt (eine Rundung, half-up); eine Cent-Zwischenrundung würde an Grenzen die höhere Endung wählen.
  const product = baseEur * factor;
  if (input.round === undefined) {
    // Ohne Endung (Story 5.60): eine Rundung half-up – auf Cent bzw. auf 4 Nachkommastellen.
    const cents = divHalfUp(product, 10n ** 6n);
    const units4 = divHalfUp(product, TEN4);
    if (product <= 0n || cents <= 0n || units4 <= 0n) return { kind: 'skip', reason: PURCHASE_PRICE_TEXTS.notPositive, version };
    const explain = `${steps.join('')} × ${german(factor, SCALE, 0)} = ${german(units4, SCALE, 2)} €`;
    return { kind: 'ok', cents, units4, explain, version };
  }
  const cents = roundToEnding(product, 10n ** 8n, input.round) / 10n ** 6n;
  if (product <= 0n || cents <= 0n) return { kind: 'skip', reason: PURCHASE_PRICE_TEXTS.notPositive, version };
  // Zwischenwert in Cent anzeigen; nur wenn er an einer Grenze zu einer anderen Endung führen würde,
  // mit bis zu 4 Nachkommastellen, damit die Rechnung nachvollziehbar bleibt.
  const centValue = divHalfUp(product, 10n ** 6n);
  const shown = roundToEnding(centValue, 100n, input.round) === cents ? german(centValue, 2, 2) : german(divHalfUp(product, TEN4), SCALE, 2);
  const explain = `${steps.join('')} × ${german(factor, SCALE, 0)} = ${shown} → ${german(cents, 2, 2)} €`;
  return { kind: 'ok', cents, units4: cents * 100n, explain, version };
}
